import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  RefreshCw, Download, ShieldCheck, AlertTriangle, Copy, ScanSearch,
  ChevronDown, ChevronRight, FileWarning,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtINR = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

const FLAG_META = {
  dedupe_suspected: { label: 'Possible double-entry', cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: Copy },
  overpaid: { label: 'Overpaid', cls: 'bg-rose-100 text-rose-800 border-rose-200', icon: AlertTriangle },
  status_mismatch: { label: 'Status mismatch', cls: 'bg-indigo-100 text-indigo-800 border-indigo-200', icon: FileWarning },
};

function StatTile({ label, value, tone = 'slate', testid }) {
  const tones = {
    slate: 'text-slate-900', teal: 'text-teal-700', amber: 'text-amber-700',
    rose: 'text-rose-700', indigo: 'text-indigo-700',
  };
  return (
    <Card className="p-4" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${tones[tone]}`}>{value}</p>
    </Card>
  );
}

function FlagRow({ trip }) {
  const [open, setOpen] = useState(false);
  const hasDetail = (trip.invoiceMismatches || []).length > 0 || (trip.dedupeSuspects || []).length > 0;
  return (
    <>
      <TableRow className="cursor-pointer hover:bg-slate-50" onClick={() => hasDetail && setOpen((o) => !o)} data-testid={`recon-row-${trip.displayTripId}`}>
        <TableCell className="font-medium text-slate-800">
          <div className="flex items-center gap-1.5">
            {hasDetail ? (open ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />) : <span className="w-3.5" />}
            <div>
              <div className="font-mono text-xs">{trip.displayTripId}</div>
              <div className="text-xs text-slate-500">{trip.guestName || '—'}{trip.destination ? ` · ${trip.destination}` : ''}</div>
            </div>
          </div>
        </TableCell>
        <TableCell>
          <div className="flex flex-wrap gap-1">
            {(trip.flags || []).map((f) => {
              const m = FLAG_META[f] || { label: f, cls: 'bg-slate-100 text-slate-700 border-slate-200', icon: AlertTriangle };
              const Icon = m.icon;
              return (
                <Badge key={f} variant="outline" className={`${m.cls} text-[10px] gap-1`}>
                  <Icon className="h-3 w-3" /> {m.label}
                </Badge>
              );
            })}
          </div>
        </TableCell>
        <TableCell className="text-right tabular-nums">{trip.currencyMode === 'FX' ? `${trip.currency} ${Math.round(trip.payableForeign || 0).toLocaleString()}` : fmtINR(trip.payable)}</TableCell>
        <TableCell className="text-right tabular-nums text-emerald-700">{trip.currencyMode === 'FX' ? `${trip.currency} ${Math.round(trip.receivedForeign || 0).toLocaleString()}` : fmtINR(trip.received)}</TableCell>
        <TableCell className="text-right tabular-nums">{fmtINR(trip.invoiced)}</TableCell>
        <TableCell className={`text-right tabular-nums font-semibold ${trip.overpaid ? 'text-amber-700' : ((trip.currencyMode === 'FX' ? trip.outstandingForeign : trip.outstanding) > 0 ? 'text-rose-700' : 'text-emerald-700')}`}>
          {trip.currencyMode === 'FX'
            ? `${trip.currency} ${Math.round(trip.outstandingForeign || 0).toLocaleString()}`
            : (trip.overpaid ? `+${fmtINR(trip.overpaidBy)}` : fmtINR(trip.outstanding))}
        </TableCell>
      </TableRow>
      {open && hasDetail && (
        <TableRow className="bg-slate-50/60" data-testid={`recon-detail-${trip.displayTripId}`}>
          <TableCell colSpan={6} className="text-xs">
            <div className="space-y-2 py-1">
              {(trip.invoiceMismatches || []).map((m, i) => (
                <div key={`m${i}`} className="flex items-start gap-2">
                  <FileWarning className="h-3.5 w-3.5 text-indigo-500 mt-0.5 shrink-0" />
                  <span className="font-mono text-slate-700">{m.invoiceNumber}</span>
                  <span className="text-slate-600">— {(m.reasons || []).join('; ')}</span>
                </div>
              ))}
              {(trip.dedupeSuspects || []).map((s, i) => (
                <div key={`s${i}`} className="flex items-start gap-2">
                  <Copy className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />
                  <span className="text-slate-600">
                    {fmtINR(s.amount)} recorded as receipt <span className="font-mono">{s.receiptId}</span> ({s.receiptDate || 'no date'}) AND as an unlinked payment on invoice <span className="font-mono">{s.invoiceNumber}</span> ({s.paymentDate || 'no date'}){s.sameDate ? ' — same date' : ''}. Verify it is not the same money entered twice.
                  </span>
                </div>
              ))}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default function ReconciliationReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/invoices/reconciliation-report`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to run reconciliation report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/invoices/reconciliation-report?format=csv`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `reconciliation_report_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('CSV export failed');
    } finally {
      setDownloading(false);
    }
  };

  const counts = data?.counts || {};
  const flagged = data?.flagged || [];

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="reconciliation-report-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <ScanSearch className="h-5 w-5 text-teal-600" />
            <h1 className="text-2xl font-bold text-slate-900">Reconciliation Report</h1>
          </div>
          <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
            Read-only audit — nothing is changed. Review each flag and correct it manually if needed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="recon-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Re-run
          </Button>
          <Button onClick={downloadCsv} disabled={downloading || !flagged.length} className="bg-teal-600 hover:bg-teal-700" data-testid="recon-export-btn">
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Trips scanned" value={data?.tripsScanned ?? '—'} testid="recon-stat-scanned" />
        <StatTile label="Flagged" value={data?.flaggedCount ?? '—'} tone={flagged.length ? 'rose' : 'teal'} testid="recon-stat-flagged" />
        <StatTile label="Overpaid" value={counts.overpaid ?? 0} tone="amber" testid="recon-stat-overpaid" />
        <StatTile label="Possible double-entry" value={counts.dedupeSuspected ?? 0} tone="amber" testid="recon-stat-dedupe" />
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500" data-testid="recon-loading">Running reconciliation across all trips…</div>
        ) : flagged.length === 0 ? (
          <div className="p-12 text-center" data-testid="recon-empty">
            <ShieldCheck className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
            <p className="text-slate-700 font-medium">Everything reconciles</p>
            <p className="text-sm text-slate-500 mt-1">No status mismatches, overpayments, or possible double-entries were found.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Trip</TableHead>
                <TableHead>Flags</TableHead>
                <TableHead className="text-right">Payable</TableHead>
                <TableHead className="text-right">Received</TableHead>
                <TableHead className="text-right">Invoiced</TableHead>
                <TableHead className="text-right">Outstanding</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {flagged.map((t) => <FlagRow key={t.tripId || t.displayTripId} trip={t} />)}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
